import { TranslateService } from '@ngx-translate/core';
import { CodeLanguage } from './../../core/model/dictionary';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { Code, Dictionary } from 'src/app/core/model/dictionary';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';

@Component({
  standalone: false,
  selector: 'app-codeset-language-edit',
  templateUrl: './codeset-language-edit.component.html',
  styleUrls: ['./codeset-language-edit.component.scss']
})
export class CodesetLanguageEditComponent implements OnInit
{
  @Input() public dataItem: any;
  @Input() public codeGridAdvanced: boolean;

  public pageSizes = [5, 10, 25, 50, 100];

  /**
   * All language codes
   */
  public languages = [];

  /**
   * FormGroup for in-grid editing of code language.
   */
  public codeLanguageFormGroup: any = null;

  /**
   * Kendo grid view for code languages.
   */
  public codeLanguageGrid: GridViewManager;

  /**
   * The selected code.
   */
  public selectedCodeGrid: Code = null;

  /**
   * The selected code language.
   */
  public selectedCodeLanguageGrid: CodeLanguage = null;

  /**
   * Total number of code languages for the selected code set.
   */
  public codeLanguageCount = 0;

  /**
   * Error message to display in the UI.
   */
  public errorMessage: string = null;

  /**
   * Controls the code set deletion confirmation dialog.
   */
  public codeLanguageDeleteDialogOpened = false;

  /**
   * Translation message when CodeSet deletion failed
   */
  public failedSaveCodeLanguageError: string;

  /**
   * Filtered list of languages for drop-down.
   */
  public languagesFiltered: Dictionary[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for language virtualization. This is to accelerate the DOM performance.
   */
  public virtualLanguage: any = {
    itemHeight: 30
  };

  /**
   * Indicate if this is a new code language.
   */
  public isNew = false;

  constructor(
    private appConfigService: AppConfigService,
    private logger: NGXLogger,
    private formBuilder: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private dictionaryService: DictionaryService)
  {
    this.codeLanguageGrid = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  public ngOnInit(): void
  {
    this.translate.get(['CODE-LANGUAGE-FK-ERROR']).subscribe({
      next: (value) =>
      {
        this.failedSaveCodeLanguageError = value['CODE-LANGUAGE-FK-ERROR'];
      }
    });

    this.selectedCodeGrid = this.dataItem;
    this.dictionaryService.getCachedLanguages().subscribe({
      next: (data) =>
      {
        this.languages = data.filter(l => l.code !== 'en'); // Prevent selecting English from other languages
        this.filterLanguages('');
      }
    });
    this.getCodeLanguages(this.dataItem.id);
  }

  /**
   * Saves the edited code language from the grid.
   *
   * @param event Save row edit event from the Kendo grid for Code.
   */
  public saveCodeLanguage(event): void
  {
    const codeLanguage = event.formGroup.value;
    codeLanguage.dictionary = { id: this.dataItem.id };

    if (event.isNew)
    {
      codeLanguage.language = this.codeLanguageFormGroup.get('language').value;
    }
    else
    {
      codeLanguage.language = { id: this.selectedCodeLanguageGrid.language.id };
    }

    delete codeLanguage.editing;

    // call DictionaryService to create the new code record on the server.
    this.dictionaryService.saveCodeLanguage(codeLanguage, event.isNew, codeLanguage.id).subscribe({
      next: (data) =>
      {
        this.messageHandlerService.displaySuccess('CODE-LANGUAGE-SAVED-SUCCESSFULLY');

        // update the item with the server generated ID
        event.dataItem = data;

        // close the editing row, and update the grid.
        this.codeLanguageGrid.saveHandler(event);
        this.refreshCodeLanguageCount();
      },
      error: error =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
        this.codeLanguageGrid.cancelHandler(event);
      }
    });
  }

  /**
   * Handles the add new row event of the Kendo grid for Code.
   * Adds a new row to the grid, and creates a FormGroup for data input.
   */
  public codeLanguageAddHandler(event: GridEventHandler): void
  {
    this.isNew = true;
    this.codeLanguageFormGroup = this.formBuilder.group(
      {
        displayName: ['', Validators.required],
        language: [null, Validators.required],
        editing: true
      }
    );

    event.sender.addRow(this.codeLanguageFormGroup);
  }

  public codeLanguageEditHandler(event: GridEventHandler): void
  {
    this.isNew = false;
    this.selectedCodeLanguageGrid = event.dataItem;
    this.codeLanguageFormGroup = this.formBuilder.group(
      {
        id: event.dataItem.id,
        displayName: [event.dataItem.displayName, Validators.required],
        version: event.dataItem.version,
        editing: true
      }
    );

    event.sender.editRow(event.rowIndex, this.codeLanguageFormGroup);
  }

  /**
   * Opens the delete code languange confirmation dialog.
   *
   * @param codeLanguage The Code language to delete.
   */
  public openDeleteCodeLanguageDialog(codeLanguage: CodeLanguage): void
  {
    this.errorMessage = null;
    this.selectedCodeLanguageGrid = codeLanguage;
    this.codeLanguageDeleteDialogOpened = true;
  }

  /**
   * Deletes a Code Language.
   *
   * @param codeLanguage Code Language to delete.
   */
  public deleteCodeLanguage(codeLanguage: CodeLanguage): void
  {
    this.dictionaryService.deleteCodeLanguage(codeLanguage.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CODE-LANGUAGE-DELETED-SUCCESSFULLY');

        this.codeLanguageGrid.removeRow(codeLanguage);
        this.refreshCodeLanguageCount();
        this.codeLanguageDeleteDialogOpened = false;
        this.selectedCodeLanguageGrid = null;
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.errorMessage = this.failedSaveCodeLanguageError;
      }
    });
  }

  /**
   * Filters the languages based on user input.
   */
  public filterLanguages(filter: string): void
  {
    this.searchTerm = filter;
    this.languagesFiltered = this.languages
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Handles the row selection change of the Kendo grid for Code Language.
   *
   * @param selections Kendo grid selected keys.
   */
  private getCodeLanguages(id: number): void
  {
    this.codeLanguageGrid.startLoading();
    this.dictionaryService.getCodeLanguagesByDictionaryId(id).subscribe({
      next: (data) =>
      {
        this.codeLanguageGrid.setData(data);
        this.refreshCodeLanguageCount();
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.codeLanguageGrid.stopLoading();
      }
    });
  }

  /**
   * Refresh Code Languages Grid Count
   */
  private refreshCodeLanguageCount(): void
  {
    this.codeLanguageCount = this.codeLanguageGrid.data.length;
  }

}
